import { Directive } from '@angular/core';

import { Stripe } from '@df/module-stripe/stripe.namespace';
import { AbstractStripeComponent } from '@df/module-stripe/abstract-stripe.component';

import { Status } from '@df/core/status';
import { Checkout } from '@df/checkout/checkout.interfaces';

@Directive()
// tslint:disable-next-line: directive-class-suffix
export abstract class AbstractStripeCardComponent<T extends Stripe.ICheckoutPaymentMethod> extends AbstractStripeComponent<
    T,
    Stripe.ICheckoutPaymentMethodConfig
> {
    /**
     * component validation model
     * @type {boolean}
     */
    valid = false;
    /**
     * Method code
     */
    protected readonly methodCode = Stripe.PaymentMethod.StripePayments;

    /**
     * Authentication status
     */
    private authenticationStatus = new Status();

    /*
     * Flag if classes was updated to prevernt permanent update
     */
    private updateSubmittedFlag = false;

    /**
     * Submitted model
     * reset on selected method change
     * @type {boolean}
     */
    private _submitted = false;

    override ngDoCheck() {
        if (!this.isSelected()) {
            if (!this.updateSubmittedFlag) {
                this.submitted = false;
                this.updateSubmittedFlag = true;
            }
        } else {
            this.updateSubmittedFlag = false;
        }
    }

    /**
     * On submitted change callback
     */
    protected onSubmittedChange(): void {}

    /**
     * Submitted getter
     * @returns {boolean}
     */
    get submitted(): boolean {
        return this._submitted;
    }

    /**
     * Submitted setter
     * @param {boolean} value
     */
    set submitted(value: boolean) {
        this._submitted = value;
        this.onSubmittedChange();
    }

    //////////////////////////////////////////
    /////
    /////   Stripe methods
    /////
    //////////////////////////////////////////

    /**
     * On stripe correctly created callback
     */
    protected onStripeCreated(): void {
        this.subscriptions.add(
            this.checkoutStepComponent.submittedSubject.subscribe((submitted: boolean) => {
                if (submitted) {
                    this.onStepSubmitted();
                }
            })
        );
    }

    //////////////////////////////////////////
    /////
    /////   Checkout
    /////
    //////////////////////////////////////////

    /**
     * On checkout step submission callback
     */
    protected onStepSubmitted(): void {
        if (this.isSelected()) {
            this.submitted = true;
            this.detectChanges();
        }
    }

    /**
     * On checkout submit callback
     * @param {Checkout.ISubmitBeforeEventData} checkoutSubmitBeforeEventData
     * @returns {false | void}
     */
    protected override onCheckoutSubmit(checkoutSubmitBeforeEventData: Checkout.ISubmitBeforeEventData): false | void {
        if (super.onCheckoutSubmit(checkoutSubmitBeforeEventData) === false) {
            return false;
        }
        if (!this.authenticationStatus.busy) {
            checkoutSubmitBeforeEventData.continueSubmission = false;
            this.submitted = true;
            if (this.valid) {
                const status = this.checkoutFormComponent.status;

                status.busy = true;

                this.getToken()
                    .then(token => {
                        const submissionData = checkoutSubmitBeforeEventData.data;
                        submissionData.paymentMethod = this.getSubmissionPaymentData(token);
                        status.data.busy = false;
                        checkoutSubmitBeforeEventData.next();
                        const stripe = this.stripe;
                        if (stripe) {
                            this.stripeService.checkAuthentication(
                                stripe,
                                this.authenticationStatus,
                                status,
                                () => {
                                    status.data.busy = false;
                                    this.checkoutFormComponent.submitWithData(submissionData, undefined, undefined, true);
                                    this.authenticationStatus.reset();
                                },
                                e => {
                                    status.error = e || Stripe.AUTH_ERROR_MESSAGE;
                                    this.authenticationStatus.reset();
                                }
                            );
                        }
                    })
                    .catch(e => {
                        if (typeof e === 'string' && e) {
                            status.error = e;
                        } else {
                            status.error = this.getError();
                        }
                    });
            }
        }
    }

    /**
     * Returns checkout submission payment data
     * @param {string} token
     * @returns {Stripe.ICardSubmissionData}
     */
    protected getSubmissionPaymentData(token: string): Stripe.ICardSubmissionData {
        const data: Stripe.ICardSubmissionData = {
            method: this.methodCode,
            additional_data: {
                cc_stripejs_token: token
            }
        };

        return data;
    }

    protected abstract getToken(): Promise<string>;
}
